/**
 * Shared formatter for cells rendered in the Advanced Hunting result table
 * (IncidentDetail + IncidentInvestigation pages).
 *
 * Goals:
 *   - Render primitives (string/number/boolean) as their natural form
 *   - Render ISO-timestamp strings in a more readable local-time short form
 *   - Render objects/arrays as compact JSON instead of "[object Object]"
 *   - Render null/undefined as empty string (caller renders '-' for missing)
 *
 * Output is a single line string. Callers wrap with truncate + title-on-hover
 * for the full value.
 */

/**
 * Matches an ISO-8601 datetime, capturing the datetime body (group 1) and an
 * optional timezone designator (group 2 — `Z` or a ±HH:MM offset). Shared by
 * the local-time table formatter and the CSV exporter so both agree on what
 * counts as a timestamp and how a designator-less (timezone-naive) value is
 * pinned to the UTC instant it actually represents.
 */
import { localTimeZoneLabel } from './utils';

const ISO_DATETIME_RE =
  /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?)(Z|[+-]\d{2}:?\d{2})?$/;

export function formatHuntingCellValue(value: unknown): string {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') {
    // Heuristic: looks like an ISO-8601 timestamp? Render in the analyst's
    // local time. Defender's KQL `Timestamp` column is the most common case
    // and is much more useful as a short local-time string than a raw UTC
    // ISO string.
    //
    // Kusto/Defender `datetime` values are ALWAYS UTC, but Microsoft Graph's
    // runHuntingQuery serialises them WITHOUT a timezone designator
    // (e.g. "2026-05-18T06:14:09.1234567"). `new Date()` parses a
    // designator-less ISO datetime as *local* time, which silently cancels
    // the UTC->local conversion below and makes every cell render in UTC. So
    // when no `Z`/offset is present we append `Z` to pin the string to the
    // UTC instant it actually represents before converting to local time.
    const m = value.match(ISO_DATETIME_RE);
    if (m) {
      const d = new Date(m[2] ? value : `${m[1]}Z`);
      if (!Number.isNaN(d.getTime())) {
        const pad = (n: number) => String(n).padStart(2, '0');
        // Zone suffix (AB#29): the value was converted from Defender's UTC to
        // the analyst's local time, so say which zone the cell is in.
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())} ${localTimeZoneLabel(d)}`;
      }
    }
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  // Object / array — render as compact JSON. Defender Advanced Hunting
  // returns dynamic columns as JSON strings, but some KQL transforms
  // (todynamic, parse_json) yield real objects. Stringify so the cell
  // is human-readable; hover title shows the full JSON.
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Pin a Defender hunting timestamp to explicit UTC for machine-readable export.
 *
 * Microsoft Graph's runHuntingQuery serialises Defender's UTC `datetime`
 * columns WITHOUT a timezone designator (e.g. "2026-05-18T06:14:09.1234567").
 * Written to a CSV verbatim those read as bare wall-clock time: a downstream
 * tool can't tell they're UTC, and an analyst correlating them against the
 * local-time result table is silently off by the UTC offset (10-11h in AU).
 * Appending a `Z` preserves the exact instant but makes it unambiguous, which
 * is what cross-tool / SIEM correlation needs from an export.
 *
 * Only timezone-naive ISO datetimes are rewritten. Strings that already carry
 * a `Z` or ±HH:MM offset, and every non-string value, pass through untouched
 * so the caller stringifies them exactly as it did before.
 */
export function ensureUtcDesignator(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const m = value.match(ISO_DATETIME_RE);
  return m && !m[2] ? `${value}Z` : value;
}
